import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import selfQrService from '../services/selfQr.service';
import type { SelfQrInstance } from '../types/self-qr.types';
import { usePermissions } from '../utils/usePermissions';
import { useAppFeatures } from './AppFeaturesContext';
import { safeGetItem, safeSetItem, safeRemoveItem, safeGetJson, safeSetJson } from '../utils/safeStorage';
import { SelfQrApiError } from '../utils/selfQrErrorParser';

interface SelfQrContextType {
  instances: SelfQrInstance[];
  currentInstance: SelfQrInstance | null;
  isLoading: boolean;
  error: string | null;
  setCurrentInstance: (instance: SelfQrInstance | null) => void;
  refreshInstances: () => Promise<void>;
}

const SelfQrContext = createContext<SelfQrContextType | null>(null);

const STORAGE_KEY = 'self_qr_selected_instance_id';
const STORAGE_KEY_DATA = 'self_qr_selected_instance_data';

const readStoredInstance = (): SelfQrInstance | null => {
  if (typeof window === 'undefined') return null;
  const parsed = safeGetJson<SelfQrInstance | null>(STORAGE_KEY_DATA, null);
  if (
    parsed !== null &&
    typeof parsed === 'object' &&
    typeof parsed.id === 'number' &&
    typeof parsed.name === 'string' &&
    typeof parsed.is_active === 'boolean'
  ) {
    return parsed;
  }
  return null;
};

export const SelfQrProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [instances, setInstances] = useState<SelfQrInstance[]>([]);
  const [currentInstance, setCurrentInstanceState] = useState<SelfQrInstance | null>(() => readStoredInstance());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const appFeatures = useAppFeatures();
  const canList = hasPermission('integrations:self_qr:show_module') || hasPermission('integrations:self_qr:read');

  const loadInstances = useCallback(async () => {
    if (!appFeatures.self_qr) {
      setInstances([]);
      setCurrentInstanceState(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (!canList) {
      setInstances([]);
      setCurrentInstanceState(null);
      setError('Недостаточно прав для просмотра WhatsApp self-QR');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await selfQrService.listInstances();
      setInstances(data);

      // Приоритет выбора:
      // 1. Сохранённый в localStorage (если есть и активен)
      // 2. Первый активный
      // 3. Первый в списке
      let selected: SelfQrInstance | null = null;
      if (data.length > 0) {
        const savedId = safeGetItem(STORAGE_KEY);
        if (savedId) {
          const saved = data.find((i) => i.id === Number(savedId) && i.is_active);
          if (saved) selected = saved;
        }
        if (!selected) selected = data.find((i) => i.is_active) || data[0];
      }
      setCurrentInstanceState(selected);

      if (selected) {
        safeSetItem(STORAGE_KEY, String(selected.id));
        safeSetJson(STORAGE_KEY_DATA, selected);
      } else {
        safeRemoveItem(STORAGE_KEY);
        safeRemoveItem(STORAGE_KEY_DATA);
      }
    } catch (err: unknown) {
      console.error('[SelfQrContext] Failed to load instances:', err);
      const msg = err instanceof SelfQrApiError
        ? err.message
        : (err instanceof Error ? err.message : 'Не удалось загрузить список инстансов');
      setError(msg);
      setInstances([]);
      setCurrentInstanceState(null);
    } finally {
      setIsLoading(false);
    }
  }, [appFeatures.self_qr, canList]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  const setCurrentInstance = useCallback((instance: SelfQrInstance | null) => {
    setCurrentInstanceState(instance);
    if (instance) {
      safeSetItem(STORAGE_KEY, String(instance.id));
      safeSetJson(STORAGE_KEY_DATA, instance);
    } else {
      safeRemoveItem(STORAGE_KEY);
      safeRemoveItem(STORAGE_KEY_DATA);
    }
  }, []);

  const refreshInstances = useCallback(async () => {
    await loadInstances();
  }, [loadInstances]);

  const value = useMemo<SelfQrContextType>(() => ({
    instances,
    currentInstance,
    isLoading,
    error,
    setCurrentInstance,
    refreshInstances,
  }), [instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances]);

  return (
    <SelfQrContext.Provider value={value}>
      {children}
    </SelfQrContext.Provider>
  );
};

export const useSelfQr = (): SelfQrContextType => {
  const context = useContext(SelfQrContext);
  if (!context) {
    throw new Error('useSelfQr must be used within SelfQrProvider');
  }
  return context;
};
